import { useState, useRef, useEffect, useContext } from "react";
import { Modal, Box, Tabs, Tab, AppBar } from "@material-ui/core";
import { useStyles } from "../Styles/styles";
import { withStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import PaginatedTableWrapper from "./PaginatedTableWrapper";
import { UserContext } from "../Contexts/UserContext";

import { useLazyQuery, useMutation } from "@apollo/client";

import { ADD_OR_UPDATE_PERSON_IN_INCIDENT } from "../Mutations/AddOrUpdatePersonInIncident";
import {
  addressIncidentPeopleHeaders,
  addressPersonHistoryHeaders,
  homeAddressHeaders,
  mailingAddressHeaders,
  parseAddressIncidentPeople,
  parseAddressPersonHistory,
  parseHomeAddressPerson,
  parseMailingAddressPerson,
} from "../utils/AddressPeopleHelper";
import PersonHistoryModal from "./PersonHistoryModal";
import { GET_PEOPLE_WITH_HOME_ADDRESS } from "../Queries/GetPeopleWithHomeAddress";
import { GET_PEOPLE_WITH_MAILING_ADDRESS } from "../Queries/GetPeopleWithMailingAddress";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { GET_ADDRESS_PERSON_HISTORY } from "../Queries/GetAddressPersonHistory";
import { GET_ADDRESS_INCIDENT_PEOPLE } from "../Queries/GetAddressIncidentPeople";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={0}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const AntTabs = withStyles({
  root: {
    borderBottom: "none",
  },
  indicator: {
    display: "none",
  },
})(Tabs);

const AntTab = withStyles(theme => ({
  root: {
    backgroundColor: "lightgrey",
    "&:hover": {
      color: "#d9833b",
      opacity: 1,
    },
    "&$selected": {
      color: "white",
      backgroundColor: "rgb(48, 62, 79)",
      fontWeight: theme.typography.fontWeightMedium,
      border: "none",
      "& > span > div > svg": {
        color: "white",
        "&:hover": {
          color: "#d9833b !important",
        },
      },
    },
    marginRight: "5px",
  },
  selected: {},
}))(props => <Tab disableRipple {...props} />);

const AddressPeopleModal = ({
  modalOpen,
  setModalOpen,
  address,
  handleIncidentOpening,
  handleTicketOpening,
  handlePersonOpening,
  incidentId,
  incidentPeople = [],
}) => {
  const classes = useStyles();
  const [value, setValue] = useState(0);
  const [personForHistory, setPersonForHistory] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const { user } = useContext(UserContext);
  const { incidentDispatch } = useContext(IncidentsContext);
  const { ADD_OR_UPDATE_PIS } = INCIDENT_ACTIONS;
  const tabWrapperRef = useRef(null);
  const appBarRef = useRef(null);

  const dashSize = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  useEffect(() => {
    if (personForHistory) {
      setShowHistoryModal(true);
    }
  }, [personForHistory, setShowHistoryModal]);

  const closePersonHistoryModal = () => {
    setShowHistoryModal(false);
    setPersonForHistory(null);
  };

  const handleAddPersonMatchToIncidentState = (
    incidentId,
    personIncidentSpecific
  ) => {
    incidentDispatch({
      type: ADD_OR_UPDATE_PIS,
      payload: {
        incidentId: incidentId,
        pis: personIncidentSpecific,
      },
    });
  };

  const [addPersonToIncident] = useMutation(ADD_OR_UPDATE_PERSON_IN_INCIDENT, {
    onCompleted: data => {
      if (data) {
        handleAddPersonMatchToIncidentState(
          data.addOrUpdatePersonInIncident.incidentUuid,
          data.addOrUpdatePersonInIncident.personIncidentSpecific
        );
        setModalOpen(false);
      }
    },
  });

  const handleAddPersonToIncident = (person, incidentId) => {
    if (!person?.id || !incidentId) {
      console.error(
        "Invalid person or incidentId in handleAddPersonToIncident"
      );
      return;
    }
    const personAlreadyOnIncident = incidentPeople.some(
      p => p.id === person?.id
    );
    if (personAlreadyOnIncident) {
      alert("This person is already on the incident");
      return;
    }
    addPersonToIncident({
      variables: {
        input: {
          originatorId: user?.id,
          incidentId: incidentId,
          personId: person.id,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
  };

  const handleTabChange = newValue => {
    setValue(newValue);
    switch (newValue) {
      case 0:
        getPeopleWithHomeAddress();
        break;
      case 1:
        getPeopleWithMailingAddress();
        break;
      case 2:
        getAddressPersonHistory();
        break;
      case 3:
        getAddressIncidentPeople();
        break;

      default:
        return null;
    }
  };

  useEffect(() => {
    if (modalOpen && value === 0 && address?.id) {
      getPeopleWithHomeAddress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, address?.id, value]);

  const [
    getPeopleWithHomeAddress,
    {
      loading: peopleWithHomeAddressLoading,
      data: peopleWithHomeAddressData,
      error: peopleWithHomeAddressError,
    },
  ] = useLazyQuery(GET_PEOPLE_WITH_HOME_ADDRESS, {
    variables: { originatorId: user?.id, addressId: address?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
  });
  const [
    getPeopleWithMailingAddress,
    {
      loading: peopleWithMailingAddressLoading,
      data: peopleWithMailingAddressData,
      error: peopleWithMailingAddressError,
    },
  ] = useLazyQuery(GET_PEOPLE_WITH_MAILING_ADDRESS, {
    variables: { originatorId: user?.id, addressId: address?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
  });
  const [
    getAddressPersonHistory,
    {
      loading: addressHistoryPersonLoading,
      data: addressHistoryPersonData,
      error: addressHistoryPersonError,
    },
  ] = useLazyQuery(GET_ADDRESS_PERSON_HISTORY, {
    variables: { originatorId: user?.id, addressId: address?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
  });

  const [
    getAddressIncidentPeople,
    {
      loading: addressIncidentPeopleLoading,
      data: addressIncidentPeopleData,
      error: addressIncidentPeopleError,
    },
  ] = useLazyQuery(GET_ADDRESS_INCIDENT_PEOPLE, {
    variables: { originatorId: user?.id, addressId: address?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
  });

  return (
    <Modal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      className={classes.modal}
      data-cy="address-history-modal"
      keepMounted={false}
    >
      <div
        id={"tabs-wrapper"}
        style={{ height: "75vh", maxHeight: "75vh", maxWidth: "75vw" }}
        className={classes.tabRoot}
        ref={tabWrapperRef}
      >
        <AppBar position="static" color="default" ref={appBarRef}>
          <AntTabs
            variant="scrollable"
            scrollButtons="auto"
            value={value}
            onChange={(e, newValue) => handleTabChange(newValue)}
            aria-label="simple tabs example"
          >
            <AntTab
              id={"person-with-home-address-tab"}
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                border: "1px solid rgb(48, 62, 79)",
                borderBottom: "none",
              }}
              label={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  Home Address
                </div>
              }
              {...a11yProps(0)}
            />
            <AntTab
              id={"person-with-mailing-address-tab"}
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                border: "1px solid rgb(48, 62, 79)",
                borderBottom: "none",
              }}
              label={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  Mailing Address
                </div>
              }
              {...a11yProps(1)}
            />
            <AntTab
              id={"address-history-tab"}
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                border: "1px solid rgb(48, 62, 79)",
                borderBottom: "none",
              }}
              label={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  Address History
                </div>
              }
              {...a11yProps(2)}
            />
            <AntTab
              id={"incident-history-tab"}
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                border: "1px solid rgb(48, 62, 79)",
                borderBottom: "none",
              }}
              label={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  Incident Address
                </div>
              }
              {...a11yProps(3)}
            />
          </AntTabs>
        </AppBar>
        <TabPanel
          id="person-with-home-address-tab-panel"
          value={value}
          index={0}
          style={{ height: "100%", overflowY: "auto" }}
        >
          <PaginatedTableWrapper
            dashSize={dashSize}
            dashWidthPercentage={0.75}
            tableHeight={"100%"}
            tableName={"Home Address"}
            headers={homeAddressHeaders}
            userId={user?.id}
            paginatedQueryHandler={() => console.log("Not paginated")}
            rows={
              peopleWithHomeAddressData
                ? parseHomeAddressPerson(
                    peopleWithHomeAddressData?.getAddress?.associatedPeople
                      ?.home,
                    setPersonForHistory,
                    incidentId,
                    handleAddPersonToIncident,
                    handlePersonOpening
                  )
                : []
            }
            loading={peopleWithHomeAddressLoading}
            error={peopleWithHomeAddressError}
            paginated={false}
            resizable={false}
          />
        </TabPanel>
        <TabPanel
          id="person-with-mailing-address-tab-panel"
          value={value}
          index={1}
          style={{ height: "100%", overflowY: "auto" }}
        >
          <PaginatedTableWrapper
            dashSize={dashSize}
            dashWidthPercentage={0.75}
            tableHeight={"100%"}
            tableName={"Mailing Address"}
            headers={mailingAddressHeaders}
            userId={user?.id}
            paginatedQueryHandler={() => console.log("Not paginated")}
            rows={
              peopleWithMailingAddressData
                ? parseMailingAddressPerson(
                    peopleWithMailingAddressData?.getAddress?.associatedPeople
                      ?.mailing,
                    setPersonForHistory,
                    incidentId,
                    handleAddPersonToIncident,
                    handlePersonOpening
                  )
                : []
            }
            loading={peopleWithMailingAddressLoading}
            error={peopleWithMailingAddressError}
            paginated={false}
            resizable={false}
          />
        </TabPanel>
        <TabPanel
          id="address-history-tab-panel"
          value={value}
          index={2}
          style={{ height: "100%", overflowY: "auto" }}
        >
          <PaginatedTableWrapper
            dashSize={dashSize}
            dashWidthPercentage={0.75}
            tableHeight={"100%"}
            tableName={"Address History"}
            headers={addressPersonHistoryHeaders}
            userId={user?.id}
            paginatedQueryHandler={() => console.log("Not paginated")}
            rows={
              addressHistoryPersonData
                ? parseAddressPersonHistory(
                    addressHistoryPersonData?.getAddress?.associatedPeople
                      ?.history,
                    setPersonForHistory,
                    incidentId,
                    handleAddPersonToIncident,
                    handlePersonOpening
                  )
                : []
            }
            loading={addressHistoryPersonLoading}
            error={addressHistoryPersonError}
            paginated={false}
            resizable={false}
          />
        </TabPanel>
        <TabPanel
          id="incident-history-tab-panel"
          value={value}
          index={3}
          style={{ height: "100%", overflowY: "auto" }}
        >
          <PaginatedTableWrapper
            dashSize={dashSize}
            dashWidthPercentage={0.75}
            tableHeight={"100%"}
            tableName={"Incident History"}
            headers={addressIncidentPeopleHeaders}
            userId={user?.id}
            paginatedQueryHandler={() => console.log("Not paginated")}
            rows={
              addressIncidentPeopleData
                ? parseAddressIncidentPeople(
                    addressIncidentPeopleData?.getAddress?.associatedPeople
                      ?.incidentPeople,
                    setPersonForHistory,
                    incidentId,
                    handleAddPersonToIncident,
                    handleIncidentOpening,
                    handlePersonOpening,
                    user?.userRoles
                  )
                : []
            }
            loading={addressIncidentPeopleLoading}
            error={addressIncidentPeopleError}
            paginated={false}
            resizable={false}
          />
        </TabPanel>
        <PersonHistoryModal
          modalOpen={showHistoryModal}
          setModalOpen={closePersonHistoryModal}
          person={personForHistory}
          userRoles={user?.userRoles}
          handleIncidentOpening={handleIncidentOpening}
          handleTicketOpening={handleTicketOpening}
          handlePersonOpening={handlePersonOpening}
          incidentId={incidentId}
          handleAddPersonMatchToIncidentState={() =>
            alert("handleAddPersonMatchToIncidentState not implemented")
          }
        />
      </div>
    </Modal>
  );
};
export default AddressPeopleModal;
